import React, { useState, useEffect } from 'react';
import { fetchUserById, fetchCities, fetchRestaurantInfo } from './api';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton, 
  Drawer, 
  useTheme, 
  alpha, 
  Tooltip, 
  Popover, 
  Badge,
  useMediaQuery,
  Container,
  Button,
  Chip,
  Avatar,
  SwipeableDrawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider as MuiDivider,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
/* import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'; */
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useCart } from './CartContext';
import AccountDrawer from './AccountDrawer';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import StoreIcon from '@mui/icons-material/Store';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
/* import Cart from './Cart'; */
import { useTranslation } from 'react-i18next';
import { getUnreadNotificationCount } from './notificationsApi';
import config from './config';
import HomeIcon from '@mui/icons-material/Home';

const BLACK_HEADER_HEIGHT_XS = 32;
const BLACK_HEADER_HEIGHT_SM = 26;

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [bgImage, setBgImage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };
  
  const handleNotificationClose = () => {
    setNotificationAnchor(null);
    loadUnreadCount();
  };

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/RestaurantInfo/slider`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const image = data.find(img => img.Order === 1 && img.IsActive);
          if (image) {
            const imgPath = image.ImagePath.startsWith('http') 
              ? image.ImagePath 
              : `${config.SITE_BASE_URL}${image.ImagePath.startsWith('/') ? '' : '/'}${image.ImagePath}`;
            setBgImage(imgPath);
          }
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [user, setUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  /* const [cartOpen, setCartOpen] = useState(false); */
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [cities, setCities] = useState([]);
  const { cartItems, selectedBranch } = useCart();
  const cartCount = cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  const handleLanguageChange = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };
  
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    /* setCartOpen(false); */
    setUserDrawerOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAdminRoute) {
      document.dir = i18n.dir();
    }
  }, [isAdminRoute, i18n.language]);

  const loadUnreadCount = async () => {
    if (user && (user.Id || user.id)) {
      const count = await getUnreadNotificationCount(user.Id || user.id);
      setUnreadCount(count);
    }
  };

  useEffect(() => {
    if (user) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const checkAdminUser = () => {
      const storedAdminUser = localStorage.getItem('admin_user');
      if (storedAdminUser) {
        try {
          setAdminUser(JSON.parse(storedAdminUser));
        } catch (e) {
          setAdminUser(null);
        }
      } else {
        setAdminUser(null);
      }
    };
    checkAdminUser();
    window.addEventListener('adminUserChanged', checkAdminUser);
    return () => window.removeEventListener('adminUserChanged', checkAdminUser);
  }, []);

  const fetchAndSetUser = async () => {
    const localUser = (() => {
      try {
        return JSON.parse(localStorage.getItem('user'));
      } catch {
        return null;
      }
    })();
    if (localUser && (localUser.Id || localUser.id)) {
      try {
        const fetchedUser = await fetchUserById(localUser.Id || localUser.id);
        setUser(fetchedUser);
        localStorage.setItem('user', JSON.stringify(fetchedUser));
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  };

  useEffect(() => {
    fetchAndSetUser();
    fetchRestaurantInfo().then(setRestaurantInfo).catch(console.error);
  }, []);

  useEffect(() => {
    async function getCities() {
      try {
        const data = await fetchCities();
        setCities(data);
      } catch {
        setCities([]);
      }
    }
    getCities();
  }, []);

  const navItems = [
    { 
      icon: <HomeIcon />, 
      label: t('navigation.home', 'Home'),
      onClick: () => navigate('/select-branch'),
      color: theme.palette.primary.main
    },
    { 
      icon: <FavoriteBorderIcon />, 
      label: t('favorites.title', 'Favorites'), 
      onClick: () => navigate('/favorites'),
      color: theme.palette.error.main
    },
    { 
      icon: <CardGiftcardIcon />, 
      label: t('navigation.offers', 'Offers'), 
      onClick: () => navigate('/offers'),
      color: theme.palette.success.main
    },
    { 
      icon: <StoreIcon />, 
      label: selectedBranch?.name || t('branchSelector.title', 'Branch'),
      onClick: () => navigate('/'),
      color: theme.palette.info.main,
      isChip: true
    },
  ];

  const mobileMenuItems = [
    { 
      label: t('account.title', 'Account'), 
      icon: <AccountCircleIcon />, 
      onClick: async () => { 
        await fetchAndSetUser(); 
        setUserDrawerOpen(true); 
        setMobileMenuOpen(false);
      },
      color: theme.palette.primary.main,
      gradient: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
    },
    { 
      icon: <FavoriteBorderIcon />, 
      label: t('favorites.title', 'Favorites'), 
      onClick: () => { navigate('/favorites'); setMobileMenuOpen(false); },
      color: theme.palette.error.main,
      gradient: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
    },
    { 
      icon: <StoreIcon />, 
      label: t('branchSelector.title', 'Branch'), 
      subtitle: selectedBranch?.name 
        ? `${t('branchSelector.subtitle', 'Click here to go to selected store')} - (${selectedBranch.name}) `
        : t('branchSelector.subtitle', 'Click here to go to selected store'),
      onClick: () => { navigate('/'); setMobileMenuOpen(false); },
      color: theme.palette.info.main,
      gradient: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`
    },
    { 
      icon: <StoreIcon />, 
      label: t('branchSelector.title2', 'Change stores'), 
      subtitle: t('branchSelector.select', 'Select Branch'),
      onClick: () => { navigate('/select-branch'); setMobileMenuOpen(false); },
      color: theme.palette.info.main,
      gradient: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`
    },
    { 
      icon: <CardGiftcardIcon />, 
      label: t('navigation.offers', 'Offers'), 
      onClick: () => { navigate('/offers'); setMobileMenuOpen(false); },
      color: theme.palette.success.main,
      gradient: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
    },
    { 
      icon: <NotificationsNoneIcon />, 
      label: t('account.notifications', 'Notifications'),
      badge: unreadCount,
      onClick: (e) => { handleNotificationOpen(e); setMobileMenuOpen(false); },
      color: theme.palette.warning.main,
      gradient: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
    },
    { 
      icon: <RocketLaunchIcon />, 
      label: t('navigation.joinUs', 'Join Us'),
      subtitle: t('navigation.joinUsSubtitle', 'Register your store'),
      onClick: () => { navigate('/join'); setMobileMenuOpen(false); },
      color: theme.palette.secondary.main,
      gradient: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`
    },
    ...(adminUser ? [{ 
      icon: <AdminPanelSettingsIcon />, 
      label: t('common.Dashboard', 'Admin'), 
      onClick: () => { window.location.href = '/admin'; setMobileMenuOpen(false); },
      color: theme.palette.secondary.main,
      gradient: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`
    }] : [])
  ];

  return (
    <>
      <AppBar
        position="sticky"
        elevation={scrolled ? 4 : 0}
        sx={{
          top: { xs: BLACK_HEADER_HEIGHT_XS, sm: BLACK_HEADER_HEIGHT_SM },
          zIndex: 1399,
          bgcolor: scrolled 
            ? alpha(theme.palette.background.paper, 0.95)
            : theme.palette.primary.main,
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            disableGutters
            sx={{
              minHeight: { xs: 64, md: 80 },
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            {/* Logo Section */}
            <Box 
              onClick={() => navigate('/select-branch')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
                transition: 'transform 0.3s',
                '&:hover': { transform: 'scale(1.05)' }
              }}
            >
              <Avatar
                src={
                  restaurantInfo?.LogoPath
                    ? (restaurantInfo.LogoPath.startsWith('http')
                        ? restaurantInfo.LogoPath
                        : `${config.SITE_BASE_URL}${restaurantInfo.LogoPath.startsWith('/') ? '' : '/'}${restaurantInfo.LogoPath}`)
                    : process.env.PUBLIC_URL + '/lunar-icon.png'
                }
                alt={restaurantInfo?.Name || 'Logo'}
                sx={{
                  width: { xs: 45, md: 55 },
                  height: { xs: 45, md: 55 },
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                  border: `3px solid ${theme.palette.background.paper}`,
                  bgcolor: 'white'
                }}
              />
              {!isMobile && (
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    ...(scrolled ? {
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    } : {
                      color: 'white',
                    }),
                    transition: 'all 0.3s',
                  }}
                >
                  {restaurantInfo?.Name || 'منصة ستورزي للتسّوق'}
                </Typography>
              )}
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {navItems.map((item, index) => (
                  item.isChip ? (
                    <Chip
                      key={index}
                      icon={React.cloneElement(item.icon, { 
                        sx: { color: scrolled ? item.color : 'white' }
                      })}
                      label={item.label}
                      onClick={item.onClick}
                      sx={{
                        height: 42,
                        px: 1.5,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        bgcolor: scrolled ? alpha(item.color, 0.1) : alpha('#fff', 0.2),
                        color: scrolled ? item.color : 'white',
                        border: `2px solid ${scrolled ? alpha(item.color, 0.3) : alpha('#fff', 0.4)}`,
                        transition: 'all 0.3s',
                        '&:hover': {
                          bgcolor: scrolled ? alpha(item.color, 0.2) : alpha('#fff', 0.3),
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 12px ${alpha(item.color, 0.3)}`
                        }
                      }}
                    />
                  ) : (
                    <Button
                      key={index}
                      startIcon={React.cloneElement(item.icon, { 
                        sx: { color: scrolled ? item.color : 'white' }
                      })}
                      onClick={item.onClick}
                      sx={{
                        color: scrolled ? 'text.primary' : 'white',
                        fontWeight: 600,
                        px: 2.5,
                        py: 1.2,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%) scaleX(0)',
                          width: '80%',
                          height: 3,
                          bgcolor: item.color,
                          borderRadius: '3px 3px 0 0',
                          transition: 'transform 0.3s'
                        },
                        '&:hover': {
                          bgcolor: alpha(item.color, 0.1),
                          '&::before': { transform: 'translateX(-50%) scaleX(1)' }
                        }
                      }}
                    >
                      {item.label}
                    </Button>
                  )
                ))}
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {!isMobile && (
                <Tooltip title={t('account.notifications', 'Notifications')} arrow>
                  <IconButton
                    onClick={handleNotificationOpen}
                    sx={{
                      bgcolor: scrolled ? alpha(theme.palette.warning.main, 0.1) : alpha('#fff', 0.2),
                      color: scrolled ? theme.palette.warning.main : 'white',
                      '&:hover': {
                        bgcolor: scrolled ? alpha(theme.palette.warning.main, 0.2) : alpha('#fff', 0.3),
                        transform: 'scale(1.1)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    <Badge badgeContent={unreadCount} color="error" max={99}>
                      <NotificationsNoneIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}

              {!isMobile && (
                <Tooltip title={t('account.title', 'Account')} arrow>
                  <IconButton
                    onClick={async () => { await fetchAndSetUser(); setUserDrawerOpen(true); }}
                    sx={{
                      bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.1) : alpha('#fff', 0.2),
                      color: scrolled ? theme.palette.primary.main : 'white',
                      '&:hover': {
                        bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.2) : alpha('#fff', 0.3),
                        transform: 'scale(1.1)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    <AccountCircleIcon />
                  </IconButton>
                </Tooltip>
              )}

              {!isMobile && adminUser && (
                <Tooltip title={t('common.Dashboard', 'Admin')} arrow>
                  <IconButton
                    onClick={() => window.location.href = '/admin'}
                    sx={{
                      bgcolor: scrolled ? alpha(theme.palette.secondary.main, 0.1) : alpha('#fff', 0.2),
                      color: scrolled ? theme.palette.secondary.main : 'white',
                      '&:hover': {
                        bgcolor: scrolled ? alpha(theme.palette.secondary.main, 0.2) : alpha('#fff', 0.3),
                        transform: 'scale(1.1)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    <AdminPanelSettingsIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* ✅ Desktop Join Us Button - TEXT ONLY */}
              {!isMobile && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/join')}
                  sx={{
                    background: scrolled 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : alpha('#fff', 0.25),
                    color: 'white',
                    px: 3,
                    py: 1.2,
                    borderRadius: 3,
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: scrolled 
                      ? '0 4px 14px rgba(102, 126, 234, 0.4)'
                      : `0 4px 14px ${alpha('#fff', 0.3)}`,
                    border: scrolled ? 'none' : `1px solid ${alpha('#fff', 0.4)}`,
                    backdropFilter: scrolled ? 'none' : 'blur(10px)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  {t('navigation.joinUs', 'Join Us')}
                </Button>
              )}

              {/* Desktop Cart Button - COMMENTED OUT */}
              {/* {!isMobile && (
                <Button
                  variant="contained"
                  onClick={() => setCartOpen(true)}
                  sx={{
                    bgcolor: scrolled ? theme.palette.primary.main : alpha('#fff', 0.25),
                    color: 'white',
                    px: 3,
                    py: 1.2,
                    borderRadius: 3,
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                    border: scrolled ? 'none' : `1px solid ${alpha('#fff', 0.4)}`,
                    backdropFilter: scrolled ? 'none' : 'blur(10px)',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  <Badge 
                    badgeContent={cartCount} 
                    color="error" 
                    max={99}
                    sx={{ '& .MuiBadge-badge': { top: -8, right: -12, fontWeight: 700 } }}
                  >
                    {t('cart.title', 'Cart')}
                  </Badge>
                </Button>
              )} */}

              {/* ✅ Mobile - Join Us Button TEXT ONLY (no icon) + Menu */}
              {isMobile && (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/join')}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      minWidth: 'auto',
                      px: 2,
                      py: 1,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: 2,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        transform: 'scale(1.05)'
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    {t('navigation.joinUs', 'Join Us')}
                  </Button>

                  {/* Mobile Cart Button - COMMENTED OUT */}
                  {/* <IconButton
                    onClick={() => setCartOpen(true)}
                    sx={{
                      bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.1) : alpha('#fff', 0.2),
                      color: scrolled ? theme.palette.primary.main : 'white',
                      '&:hover': {
                        bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.2) : alpha('#fff', 0.3)
                      }
                    }}
                  >
                    <Badge badgeContent={cartCount} color="error" max={99}>
                      <ShoppingCartIcon />
                    </Badge>
                  </IconButton> */}

                  <IconButton
                    onClick={() => setMobileMenuOpen(true)}
                    sx={{
                      bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.1) : alpha('#fff', 0.2),
                      color: scrolled ? theme.palette.primary.main : 'white',
                      '&:hover': {
                        bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.2) : alpha('#fff', 0.3)
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>

        {/* Background Overlay */}
        {!scrolled && bgImage && (
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.15,
              zIndex: -1,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(to bottom, ${alpha('#000', 0.5)}, ${alpha('#000', 0.2)})`,
              }
            }}
          />
        )}
      </AppBar>

      {/* Mobile Bottom Sheet */}
      <SwipeableDrawer
        anchor="bottom"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onOpen={() => setMobileMenuOpen(true)}
        disableSwipeToOpen
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '85vh',
            pb: 2,
          }
        }}
      >
        <Box sx={{ width: 40, height: 5, bgcolor: 'divider', borderRadius: 10, mx: 'auto', mt: 1.5, mb: 2 }} />
        <Box sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>{t('navigation.menu', 'Menu')}</Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)} size="small"><CloseIcon /></IconButton>
        </Box>
        <MuiDivider />
        <List sx={{ px: 2, py: 1 }}>
          {mobileMenuItems.map((item, index) => (
            <ListItemButton
              key={index}
              onClick={item.onClick}
              sx={{
                borderRadius: 3, mb: 1,
                background: item.gradient,
                color: 'white', py: 2, px: 2.5,
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateX(8px)', boxShadow: `0 4px 20px ${alpha(item.color, 0.4)}` }
              }}
            >
              <ListItemIcon sx={{ minWidth: 45, color: 'white' }}>
                <Badge badgeContent={item.badge || 0} color="error" max={99}>{item.icon}</Badge>
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                secondary={item.subtitle}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                secondaryTypographyProps={{ sx: { color: alpha('#fff', 0.8), fontSize: '0.85rem', mt: 0.5 } }}
              />
            </ListItemButton>
          ))}
        </List>
      </SwipeableDrawer>

      {/* Notification Popover */}
      <Popover 
        open={Boolean(notificationAnchor)} 
        anchorEl={notificationAnchor} 
        onClose={handleNotificationClose} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} 
        transformOrigin={{ vertical: 'top', horizontal: 'right' }} 
        PaperProps={{ 
          sx: { minWidth: { xs: 280, sm: 320 }, maxWidth: 400, p: 0, borderRadius: 3, boxShadow: theme.shadows[12] }, 
          dir: i18n.language === 'ar' ? 'rtl' : 'ltr' 
        }}
      >
        {React.createElement(require('./StoreNotifications').default)}
      </Popover>

      {/* Cart Drawer - COMMENTED OUT */}
      {/* <Drawer 
        anchor="right" 
        open={cartOpen} 
        onClose={() => setCartOpen(false)}
        ModalProps={{ keepMounted: true, sx: { zIndex: 1600 } }}
        PaperProps={{ 
          sx: { 
            width: { xs: '100vw', sm: '60vw', md: 420 }, 
            height: '100vh',
            top: 0,
            borderTopLeftRadius: { xs: 0, sm: 20 },
            borderBottomLeftRadius: { xs: 0, sm: 20 },
          } 
        }}
      >
        <Cart onProceed={() => setCartOpen(false)} onClose={() => setCartOpen(false)} />
      </Drawer> */}
      
      {/* Account Drawer */}
      <Drawer 
        anchor="right" 
        open={userDrawerOpen} 
        onClose={() => setUserDrawerOpen(false)}
        ModalProps={{ keepMounted: true, sx: { zIndex: 1600 } }}
        PaperProps={{ 
          sx: { 
            width: { xs: '100vw', sm: '60vw', md: 380 }, 
            height: '100vh',
            top: 0,
            borderTopLeftRadius: { xs: 0, sm: 20 },
            borderBottomLeftRadius: { xs: 0, sm: 20 },
          } 
        }}
      >
        <AccountDrawer 
          onClose={() => setUserDrawerOpen(false)} 
          user={user} setUser={setUser} 
          navigate={navigate} t={t} i18n={i18n} theme={theme} 
          cities={cities} selectedBranch={selectedBranch} 
          toggleLanguage={handleLanguageChange} 
        />
      </Drawer>
    </>
  );
}

export default Header;
